import React from 'react';
import { AlertTriangle, Clock, DollarSign, Calendar } from 'lucide-react';
import { isMobile, isTablet } from '../../utils/deviceUtils';

export const Cancellation: React.FC = () => {
  const mobile = isMobile();
  const tablet = isTablet();

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-4 sm:p-6 lg:p-8">
          <div className="text-center">
            <h1 className={`${mobile ? 'text-3xl' : tablet ? 'text-4xl' : 'text-5xl'} font-black mb-4`}>
              Cancellation Policy
            </h1>
            <p className={`${mobile ? 'text-base' : 'text-lg'} text-white/90 max-w-2xl mx-auto`}>
              Understanding our cancellation terms and refund policies for safari bookings.
            </p>
          </div>
        </div>
      </section>

      {/* Cancellation Content */}
      <section className={`${mobile ? 'py-12' : tablet ? 'py-16' : 'py-20'} bg-background-primary`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-soft p-6 sm:p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-text-secondary mb-8">
                <strong>Last updated:</strong> January 15, 2024
              </p>

              {/* Important Notice */}
              <div className="bg-accent-warning-50 border border-accent-warning-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-accent-warning-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-accent-warning-800 mb-2">Important Notice</h3>
                    <p className="text-accent-warning-700 text-sm leading-relaxed">
                      Cancellation policies may vary by operator and package. The terms below represent our general policy. 
                      Specific cancellation terms for your booking will be clearly stated in your booking confirmation.
                    </p>
                  </div>
                </div>
              </div>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  1. General Cancellation Policy
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We understand that travel plans can change. Our cancellation policy is designed to be fair to both travelers and operators while ensuring the sustainability of our safari services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  2. Cancellation Timeframes and Refunds
                </h2>
                
                <div className="space-y-6">
                  <div className="border border-border-primary rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-accent-success-100 rounded-xl flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-accent-success-600" />
                      </div>
                      <h3 className="font-semibold text-text-primary">60+ Days Before Departure</h3>
                    </div>
                    <p className="text-text-secondary mb-3">
                      <strong>Refund:</strong> Full refund minus processing fees (typically 3-5%)
                    </p>
                    <p className="text-text-secondary text-sm">
                      Processing fees cover payment gateway charges and administrative costs.
                    </p>
                  </div>

                  <div className="border border-border-primary rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-accent-warning-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-5 h-5 text-accent-warning-600" />
                      </div>
                      <h3 className="font-semibold text-text-primary">30-59 Days Before Departure</h3>
                    </div>
                    <p className="text-text-secondary mb-3">
                      <strong>Refund:</strong> 75% of total booking amount
                    </p>
                    <p className="text-text-secondary text-sm">
                      Operators begin making arrangements and may incur non-refundable costs.
                    </p>
                  </div>

                  <div className="border border-border-primary rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-accent-coral-100 rounded-xl flex items-center justify-center">
                        <AlertTriangle className="w-5 h-5 text-accent-coral-600" />
                      </div>
                      <h3 className="font-semibold text-text-primary">15-29 Days Before Departure</h3>
                    </div>
                    <p className="text-text-secondary mb-3">
                      <strong>Refund:</strong> 50% of total booking amount
                    </p>
                    <p className="text-text-secondary text-sm">
                      Significant arrangements have been made and costs incurred by operators.
                    </p>
                  </div>

                  <div className="border border-border-primary rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-accent-error-100 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-accent-error-600" />
                      </div>
                      <h3 className="font-semibold text-text-primary">Less Than 15 Days Before Departure</h3>
                    </div>
                    <p className="text-text-secondary mb-3">
                      <strong>Refund:</strong> No refund available
                    </p>
                    <p className="text-text-secondary text-sm">
                      All arrangements have been finalized and operators cannot recover costs.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  3. How to Cancel Your Booking
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  To cancel your booking, you can:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Use the cancellation option in your account dashboard</li>
                  <li>Contact our customer support team via email or phone</li>
                  <li>Send a written cancellation request to support@dalarides.com</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  <strong>Important:</strong> Cancellation requests must be received in writing. Verbal cancellations are not accepted.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  4. Refund Processing
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Refunds are processed as follows:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Refunds are processed within 5-10 business days of cancellation approval</li>
                  <li>Refunds are issued to the original payment method used for booking</li>
                  <li>Processing times may vary depending on your bank or payment provider</li>
                  <li>You will receive an email confirmation when the refund is processed</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  5. Force Majeure and Exceptional Circumstances
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  In cases of force majeure or exceptional circumstances, we may offer:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Full refunds regardless of cancellation timing</li>
                  <li>Rescheduling options without additional fees</li>
                  <li>Credit vouchers for future bookings</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Force majeure events include natural disasters, government travel restrictions, 
                  political instability, and other events beyond our reasonable control.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  6. Operator-Specific Policies
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Individual operators may have their own cancellation policies that differ from our general policy. 
                  These will be clearly communicated in your booking confirmation. Operator-specific policies may include:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Different cancellation timeframes</li>
                  <li>Varying refund percentages</li>
                  <li>Specific terms for group bookings</li>
                  <li>Special conditions for peak season bookings</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  7. Travel Insurance
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We strongly recommend purchasing comprehensive travel insurance that covers:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>Trip cancellation and interruption</li>
                  <li>Medical emergencies and evacuation</li>
                  <li>Lost or delayed baggage</li>
                  <li>Flight delays and cancellations</li>
                  <li>Personal liability coverage</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Travel insurance can provide additional protection beyond our cancellation policy.
                </p>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  8. No-Show Policy
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  If you fail to arrive for your safari without prior cancellation:
                </p>
                <ul className="list-disc pl-6 text-text-secondary space-y-2 mb-4">
                  <li>No refund will be provided</li>
                  <li>Operators may charge additional fees</li>
                  <li>Future bookings may be affected</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  9. Contact Information
                </h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  For cancellation requests or questions about this policy:
                </p>
                <div className="bg-background-secondary rounded-xl p-4 mt-4">
                  <p className="text-text-secondary">
                    <strong>Email:</strong> cancellations@dalarides.com<br />
                    <strong>Phone:</strong> +255 123 456 789<br />
                    <strong>Support Hours:</strong> Monday - Friday, 8:00 AM - 6:00 PM (EAT)<br />
                    <strong>Emergency:</strong> +255 999 888 777 (24/7)
                  </p>
                </div>
              </section>

              {/* Summary Table */}
              <section className="mb-8">
                <h2 className={`${mobile ? 'text-xl' : 'text-2xl'} font-bold text-text-primary mb-4`}>
                  Cancellation Summary
                </h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-border-primary rounded-xl overflow-hidden">
                    <thead className="bg-background-secondary">
                      <tr>
                        <th className="border border-border-primary px-4 py-3 text-left font-semibold text-text-primary">
                          Time Before Departure
                        </th>
                        <th className="border border-border-primary px-4 py-3 text-left font-semibold text-text-primary">
                          Refund Amount
                        </th>
                        <th className="border border-border-primary px-4 py-3 text-left font-semibold text-text-primary">
                          Processing Time
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">60+ days</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">Full refund minus fees</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">5-7 days</td>
                      </tr>
                      <tr className="bg-background-secondary">
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">30-59 days</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">75% refund</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">7-10 days</td>
                      </tr>
                      <tr>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">15-29 days</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">50% refund</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">7-10 days</td>
                      </tr>
                      <tr className="bg-background-secondary">
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">Less than 15 days</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">No refund</td>
                        <td className="border border-border-primary px-4 py-3 text-text-secondary">N/A</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}; 